/**
 * BountiesSection — creator's Bounties panel inside Manage Clan.
 *
 * Replaced Milestone Airdrops on 2026-02-17 as the primary community engagement
 * surface. Creator arms fun quests, sets a Points pool, picks a proof type
 * (text / URL / screenshot) and a selection mode (manual approve / first-come),
 * and reviews incoming submissions. All Points move through an escrow held on
 * the bounty document itself.
 *
 * Endpoints:
 *   GET    /api/clans/mine/bounties
 *   POST   /api/clans/mine/bounties
 *   POST   /api/clans/mine/bounties/{id}/end
 *   GET    /api/clans/mine/bounties/{id}/submissions
 *   POST   /api/clans/mine/bounties/{id}/submissions/{sid}/approve
 *   POST   /api/clans/mine/bounties/{id}/submissions/{sid}/reject
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { formatApiErrorDetail } from '../api';

const PROOF_TYPES = [
  { key: 'text',       label: 'Text answer',   hint: 'Fans type a written reply' },
  { key: 'url',        label: 'URL / link',    hint: 'Fans paste a link to their post or video' },
  { key: 'screenshot', label: 'Screenshot',    hint: 'Fans upload a proof image' },
];

const SELECTION_MODES = [
  { key: 'manual',      label: 'Manual approval',  hint: 'You review each submission and pick winners' },
  { key: 'first_come',  label: 'First to submit',  hint: 'First N valid submissions win instantly — you can still reject bad ones' },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  proof_type: 'text',
  proof_instructions: '',
  reward_pool_pts: 100,
  winner_count: 5,
  selection_mode: 'manual',
  ends_at: '',   // optional yyyy-mm-ddThh:mm
};

const fmtPts = (n) => `${Number(n || 0).toFixed(2)} pts`;

const fmtRelDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((d.getTime() - Date.now()) / 1000);
  if (s < 0) return 'ended';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m left`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h left`;
  const dd = Math.floor(h / 24);
  return `${dd}d left`;
};

export default function BountiesSection({ clanHandle, onToast }) {
  const [bounties, setBounties] = useState(null);
  const [showArm, setShowArm] = useState(false);
  const [reviewing, setReviewing] = useState(null);   // bounty being reviewed

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/clans/mine/bounties');
      setBounties(data.bounties || []);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load bounties.', kind: 'error' });
      setBounties([]);
    }
  }, [onToast]);

  useEffect(() => { load(); }, [load]);

  const endBounty = async (id) => {
    if (!window.confirm('End this bounty now? Unspent Points will be refunded to your balance.')) return;
    try {
      await api.post(`/clans/mine/bounties/${id}/end`);
      onToast?.({ msg: '✓ Bounty ended · refund credited' });
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not end bounty.', kind: 'error' });
    }
  };

  if (bounties === null) {
    return (
      <div className="mc-section-card" data-testid="mc-bounties-section">
        <div className="mc-section-head">
          <span className="mc-section-title">🎯 Bounties</span>
        </div>
        <p className="mc-section-sub">Loading…</p>
      </div>
    );
  }

  const active = bounties.filter((b) => b.status === 'active');
  const ended = bounties.filter((b) => b.status !== 'active');

  return (
    <div className="mc-section-card" data-testid="mc-bounties-section">
      <div className="mc-section-head">
        <span className="mc-section-title">🎯 Bounties</span>
        <button
          className="mc-btn-primary"
          style={{ padding: '6px 14px', fontSize: 13 }}
          onClick={() => setShowArm(true)}
          data-testid="bounty-arm-btn"
        >
          + Arm a bounty
        </button>
      </div>
      <p className="mc-section-sub">
        Turn engagement into rewards. Post a fun quest, escrow a Points prize, and pay fans as they complete it — no more waiting on X or YouTube milestones.
      </p>

      {active.length === 0 && ended.length === 0 && (
        <div style={{ padding: '20px 0', textAlign: 'center', opacity: 0.7, fontSize: 13 }} data-testid="bounties-empty">
          No bounties yet. Arm one above to spark a challenge for your holders.
        </div>
      )}

      {active.length > 0 && (
        <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
          {active.map((b) => (
            <BountyRow key={b.id} b={b} onReview={() => setReviewing(b)} onEnd={() => endBounty(b.id)} />
          ))}
        </div>
      )}

      {ended.length > 0 && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, opacity: 0.7 }}>
            {ended.length} ended bount{ended.length === 1 ? 'y' : 'ies'}
          </summary>
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {ended.map((b) => (
              <BountyRow key={b.id} b={b} onReview={() => setReviewing(b)} onEnd={null} readOnly />
            ))}
          </div>
        </details>
      )}

      {showArm && (
        <ArmBountyModal
          onClose={() => setShowArm(false)}
          onCreated={() => { setShowArm(false); load(); onToast?.({ msg: '✓ Bounty armed' }); }}
          onToast={onToast}
        />
      )}

      {reviewing && (
        <ReviewSubmissionsModal
          bounty={reviewing}
          onClose={() => { setReviewing(null); load(); }}
          onToast={onToast}
        />
      )}
    </div>
  );
}


function BountyRow({ b, onReview, onEnd, readOnly = false }) {
  const filledPct = b.winner_count ? Math.round((b.winners_selected / b.winner_count) * 100) : 0;
  const isActive = b.status === 'active';
  return (
    <div
      style={{
        padding: 12, borderRadius: 12,
        background: isActive ? 'rgba(0,180,255,0.06)' : 'rgba(151,145,172,0.06)',
        border: isActive ? '1px solid rgba(0,180,255,0.2)' : '1px solid rgba(255,255,255,0.06)',
      }}
      data-testid={`bounty-row-${b.id}`}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{b.title}</div>
          <div style={{ fontSize: 12, opacity: 0.7, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <span>{fmtPts(b.reward_per_winner_pts)} × {b.winner_count} winner{b.winner_count === 1 ? '' : 's'}</span>
            <span>•</span>
            <span>{b.selection_mode === 'manual' ? 'Manual review' : 'First-come'}</span>
            {b.ends_at && (
              <>
                <span>•</span>
                <span style={{ color: isActive ? '#00B4FF' : 'inherit' }}>{fmtRelDate(b.ends_at)}</span>
              </>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, opacity: 0.7 }}>Filled</div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{b.winners_selected} / {b.winner_count}</div>
        </div>
      </div>

      <div style={{ marginTop: 8, height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${filledPct}%`, height: '100%', background: 'linear-gradient(90deg, #00B4FF, #B584F5)' }} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button
          className="mc-btn-secondary"
          style={{ fontSize: 12, padding: '5px 12px', flex: 1 }}
          onClick={onReview}
          data-testid={`bounty-review-${b.id}`}
        >
          Review ({b.submissions_count})
        </button>
        {!readOnly && onEnd && (
          <button
            className="mc-btn-secondary"
            style={{ fontSize: 12, padding: '5px 12px' }}
            onClick={onEnd}
            data-testid={`bounty-end-${b.id}`}
          >
            End
          </button>
        )}
      </div>
    </div>
  );
}


function ArmBountyModal({ onClose, onCreated, onToast }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const perWinner = useMemo(() => {
    const pool = Number(form.reward_pool_pts || 0);
    const slots = Number(form.winner_count || 0);
    if (!pool || !slots) return 0;
    return pool / slots;
  }, [form.reward_pool_pts, form.winner_count]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (saving) return;
    if (!form.title.trim() || form.title.trim().length < 3) {
      onToast?.({ msg: 'Title must be at least 3 characters', kind: 'error' });
      return;
    }
    if (Number(form.reward_pool_pts) <= 0 || Number(form.winner_count) <= 0) {
      onToast?.({ msg: 'Reward pool and slots must be positive', kind: 'error' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        proof_type: form.proof_type,
        proof_instructions: form.proof_instructions.trim(),
        reward_pool_pts: Number(form.reward_pool_pts),
        winner_count: Number(form.winner_count),
        selection_mode: form.selection_mode,
      };
      if (form.ends_at) {
        // Convert local datetime-local input to ISO UTC
        body.ends_at = new Date(form.ends_at).toISOString();
      }
      await api.post('/clans/mine/bounties', body);
      onCreated();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not arm bounty.', kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20,
      }}
      data-testid="arm-bounty-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 520, width: '100%', maxHeight: '90vh', overflowY: 'auto',
          background: 'rgba(22,26,36,0.98)', backdropFilter: 'blur(20px)',
          borderRadius: 16, padding: 24, border: '1px solid rgba(0,180,255,0.3)',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>🎯 Arm a bounty</div>
        <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 18 }}>
          Fans holding your Bits will see this on your Clan feed and can submit proof to win.
        </div>

        <Label>Title</Label>
        <input
          className="mc-input"
          value={form.title}
          onChange={(e) => set('title', e.target.value.slice(0, 80))}
          placeholder="e.g., Share my post with 10 of your friends"
          maxLength={80}
          data-testid="arm-bounty-title"
        />

        <Label>Description (optional)</Label>
        <textarea
          className="mc-textarea"
          value={form.description}
          onChange={(e) => set('description', e.target.value.slice(0, 500))}
          placeholder="Explain the challenge, why it matters, and what makes a winning submission."
          rows={3}
          maxLength={500}
          data-testid="arm-bounty-desc"
        />

        <Label>Proof type</Label>
        <div style={{ display: 'grid', gap: 6 }}>
          {PROOF_TYPES.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => set('proof_type', p.key)}
              style={pickerStyle(form.proof_type === p.key)}
              data-testid={`arm-bounty-proof-${p.key}`}
            >
              <div style={{ fontWeight: 700, fontSize: 13 }}>{p.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{p.hint}</div>
            </button>
          ))}
        </div>

        <Label>Proof instructions (optional)</Label>
        <input
          className="mc-input"
          value={form.proof_instructions}
          onChange={(e) => set('proof_instructions', e.target.value.slice(0, 300))}
          placeholder="e.g., Upload a screenshot of your profile"
          maxLength={300}
          data-testid="arm-bounty-instructions"
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label>Total reward pool</Label>
            <input
              className="mc-input"
              type="number"
              min="1"
              step="1"
              value={form.reward_pool_pts}
              onChange={(e) => set('reward_pool_pts', e.target.value)}
              data-testid="arm-bounty-pool"
            />
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>Debited from your balance</div>
          </div>
          <div>
            <Label>Number of winners</Label>
            <input
              className="mc-input"
              type="number"
              min="1"
              max="1000"
              step="1"
              value={form.winner_count}
              onChange={(e) => set('winner_count', e.target.value)}
              data-testid="arm-bounty-slots"
            />
            <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>
              {perWinner > 0 ? `${perWinner.toFixed(2)} pts each` : 'Set pool + slots'}
            </div>
          </div>
        </div>

        <Label>Selection mode</Label>
        <div style={{ display: 'grid', gap: 6 }}>
          {SELECTION_MODES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => set('selection_mode', s.key)}
              style={pickerStyle(form.selection_mode === s.key)}
              data-testid={`arm-bounty-mode-${s.key}`}
            >
              <div style={{ fontWeight: 700, fontSize: 13 }}>{s.label}</div>
              <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{s.hint}</div>
            </button>
          ))}
        </div>

        <Label>Deadline (optional)</Label>
        <input
          className="mc-input"
          type="datetime-local"
          value={form.ends_at}
          onChange={(e) => set('ends_at', e.target.value)}
          data-testid="arm-bounty-deadline"
        />
        <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>Leave blank for open-ended. Unspent pool refunds when it ends.</div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button className="mc-btn-secondary" onClick={onClose} disabled={saving} data-testid="arm-bounty-cancel">
            Cancel
          </button>
          <button className="mc-btn-primary" onClick={submit} disabled={saving} data-testid="arm-bounty-confirm">
            {saving ? 'Arming…' : `Arm · ${fmtPts(form.reward_pool_pts)} escrowed`}
          </button>
        </div>
      </div>
    </div>
  );
}


function ReviewSubmissionsModal({ bounty, onClose, onToast }) {
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [noteBySub, setNoteBySub] = useState({});

  const load = useCallback(async () => {
    try {
      const { data: r } = await api.get(`/clans/mine/bounties/${bounty.id}/submissions`);
      setData(r);
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not load submissions.', kind: 'error' });
      setData({ submissions: [] });
    }
  }, [bounty.id, onToast]);

  useEffect(() => { load(); }, [load]);

  const act = async (sub, action) => {
    setBusyId(sub.id);
    try {
      const note = (noteBySub[sub.id] || '').slice(0, 300);
      await api.post(`/clans/mine/bounties/${bounty.id}/submissions/${sub.id}/${action}`, { reviewer_note: note });
      onToast?.({ msg: action === 'approve' ? '✓ Winner paid' : '✗ Rejected' });
      await load();
    } catch (ex) {
      onToast?.({ msg: formatApiErrorDetail(ex.response?.data?.detail) || `Could not ${action}.`, kind: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 20,
      }}
      data-testid="review-bounty-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto',
          background: 'rgba(22,26,36,0.98)', backdropFilter: 'blur(20px)',
          borderRadius: 16, padding: 24, border: '1px solid rgba(0,180,255,0.3)',
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>🎯 {bounty.title}</div>
        <div style={{ fontSize: 13, opacity: 0.75, marginBottom: 18 }}>
          {bounty.winners_selected} / {bounty.winner_count} winners · {fmtPts(bounty.reward_per_winner_pts)} each · {bounty.selection_mode === 'manual' ? 'Manual review' : 'First-come'}
        </div>

        {data === null && <div style={{ opacity: 0.7, fontSize: 13 }}>Loading…</div>}
        {data && data.submissions.length === 0 && (
          <div style={{ padding: '30px 0', textAlign: 'center', opacity: 0.6, fontSize: 13 }}>
            No submissions yet. Share your Clan link to get holders in.
          </div>
        )}

        {data && data.submissions.map((s) => (
          <div
            key={s.id}
            style={{
              padding: 12, borderRadius: 12, marginBottom: 10,
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            data-testid={`bounty-submission-${s.id}`}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                {s.fan_display_name || `@${s.fan_handle}`}
                {s.fan_handle && s.fan_display_name && <span style={{ opacity: 0.6, fontWeight: 500 }}> · @{s.fan_handle}</span>}
              </div>
              <StatusPill status={s.status} />
            </div>

            <ProofDisplay type={bounty.proof_type} content={s.proof_content} />

            {s.status === 'pending' && bounty.status === 'active' && (
              <>
                <input
                  className="mc-input"
                  style={{ marginTop: 10, fontSize: 12 }}
                  placeholder="Optional note to the fan"
                  value={noteBySub[s.id] || ''}
                  onChange={(e) => setNoteBySub((prev) => ({ ...prev, [s.id]: e.target.value }))}
                  maxLength={300}
                  data-testid={`bounty-review-note-${s.id}`}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    className="mc-btn-primary"
                    style={{ fontSize: 12, padding: '6px 14px', flex: 1 }}
                    onClick={() => act(s, 'approve')}
                    disabled={busyId === s.id}
                    data-testid={`bounty-approve-${s.id}`}
                  >
                    {busyId === s.id ? 'Paying…' : `✓ Approve · pay ${fmtPts(bounty.reward_per_winner_pts)}`}
                  </button>
                  <button
                    className="mc-btn-secondary"
                    style={{ fontSize: 12, padding: '6px 14px' }}
                    onClick={() => act(s, 'reject')}
                    disabled={busyId === s.id}
                    data-testid={`bounty-reject-${s.id}`}
                  >
                    ✗ Reject
                  </button>
                </div>
              </>
            )}

            {s.status === 'won' && bounty.selection_mode === 'first_come' && bounty.status === 'active' && (
              <button
                className="mc-btn-secondary"
                style={{ fontSize: 12, padding: '6px 14px', marginTop: 10 }}
                onClick={() => act(s, 'reject')}
                disabled={busyId === s.id}
                data-testid={`bounty-clawback-${s.id}`}
              >
                {busyId === s.id ? 'Clawing back…' : '↩ Reject (claw back reward)'}
              </button>
            )}
            {s.reviewer_note && (
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 8, fontStyle: 'italic' }}>
                Your note: "{s.reviewer_note}"
              </div>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="mc-btn-secondary" onClick={onClose} data-testid="review-bounty-close">Close</button>
        </div>
      </div>
    </div>
  );
}


function ProofDisplay({ type, content }) {
  if (type === 'url') {
    return (
      <a
        href={content}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 12, color: '#00B4FF', wordBreak: 'break-all' }}
      >
        {content}
      </a>
    );
  }
  if (type === 'screenshot' && content) {
    return (
      <a href={content} target="_blank" rel="noopener noreferrer">
        <img src={content} alt="proof" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, objectFit: 'cover' }} />
      </a>
    );
  }
  return (
    <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {content}
    </div>
  );
}


function StatusPill({ status }) {
  const map = {
    pending:  { bg: 'rgba(255,183,77,0.18)',  color: '#FFB74D', text: 'Pending' },
    won:      { bg: 'rgba(180,255,0,0.18)',   color: '#B4FF00', text: '🏆 Won' },
    rejected: { bg: 'rgba(255,87,34,0.15)',   color: '#FF7043', text: 'Rejected' },
  };
  const s = map[status] || { bg: 'rgba(255,255,255,0.08)', color: 'inherit', text: status };
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
      background: s.bg, color: s.color, whiteSpace: 'nowrap',
    }}>{s.text}</span>
  );
}


function Label({ children }) {
  return (
    <div style={{ fontSize: 11, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, marginTop: 14, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function pickerStyle(active) {
  return {
    textAlign: 'left', padding: '10px 12px', borderRadius: 10,
    border: active ? '1px solid rgba(0,180,255,0.6)' : '1px solid rgba(255,255,255,0.08)',
    background: active ? 'rgba(0,180,255,0.1)' : 'rgba(255,255,255,0.03)',
    cursor: 'pointer', color: 'inherit',
    transition: 'background 0.15s, border 0.15s',
  };
}
